import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Mail, Lock, AlertCircle, Chrome, Link2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PUBLIC_SIGNUP_ENABLED } from '../../config/signup';

const SignInForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showMagicLink, setShowMagicLink] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password
      });

      if (error) throw error;

      if (!data.user) throw new Error('No user data returned');

      // O vínculo com a organização sai do auth_id do token, não do e-mail
      // digitado: e-mail é dado mutável e pode se repetir entre perfis, então
      // casar por ele deixava a escolha da empresa à mercê do que veio do
      // formulário em vez do que o token comprova.
      const { data: profileData, error: profileError } = await supabase
        .from('user_profile')
        .select('organization_id')
        .eq('auth_id', data.user.id)
        .single();

      if (profileError) throw profileError;

      // Salvar organization_id no localStorage para ser carregado pelo contexto depois
      if (profileData?.organization_id) {
        localStorage.setItem('selectedOrganizationId', profileData.organization_id);
      }

      let redirectTo = '/';
      const lastRoute = localStorage.getItem('lastRoute');
      console.log('Valor de lastRoute antes do login:', lastRoute);
      if (lastRoute && lastRoute !== '/signin' && lastRoute !== '/signup') {
        redirectTo = lastRoute;
        localStorage.removeItem('lastRoute');
      }
      console.log('Redirecionando para:', redirectTo);
      setTimeout(() => navigate(redirectTo), 0);
    } catch (err) {
      console.error('Login error:', err);
      setError(err instanceof Error ? err.message : 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      // Construir URL de redirecionamento com hash para evitar problemas de roteamento
      const redirectUrl = `${window.location.origin}/reset-password`;

      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: redirectUrl,
      });

      if (error) throw error;

      setSuccessMessage('Email de recuperação enviado! Verifique sua caixa de entrada.');
      setResetEmail('');
      setTimeout(() => {
        setShowResetPassword(false);
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err instanceof Error ? err.message : 'Erro ao enviar email de recuperação');
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      // Usar URL de produção se não for localhost
      const redirectUrl = window.location.hostname === 'localhost'
        ? `${window.location.origin}/`
        : 'https://login.nankin.me/';

      const { error } = await supabase.auth.signInWithOtp({
        email: magicLinkEmail,
        options: {
          emailRedirectTo: redirectUrl,
        }
      });

      if (error) throw error;

      setSuccessMessage('Link mágico enviado! Verifique seu email e clique no link para entrar.');
      setMagicLinkEmail('');
      setTimeout(() => {
        setShowMagicLink(false);
        setSuccessMessage('');
      }, 5000);
    } catch (err) {
      console.error('Magic link error:', err);
      setError(err instanceof Error ? err.message : 'Erro ao enviar link mágico');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setError('');

      // Usar URL de produção se não for localhost
      const redirectUrl = window.location.hostname === 'localhost'
        ? `${window.location.origin}/`
        : 'https://login.nankin.me/';

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (error) throw error;
    } catch (err) {
      console.error('Google login error:', err);
      setError(err instanceof Error ? err.message : 'Erro ao fazer login com Google');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 relative">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-30"
        style={{ backgroundImage: 'url(/images/login-background.jpg)' }}
      />

      {/* Overlay to improve text readability */}
      <div className="absolute inset-0 bg-nk-black/20" />

      <div className="max-w-md w-full space-y-8 relative z-10">
        <div>
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img
              src="/images/logo-nankin.svg"
              alt="Nankin Logo"
              className="h-20 w-auto"
            />
          </div>

          <h2 className="mt-6 text-center text-3xl font-bold" style={{ color: 'var(--color-text)' }}>
            Entrar em sua conta
          </h2>
          {PUBLIC_SIGNUP_ENABLED && (
            <div className="mt-4 text-center">
              <span className="text-base" style={{ color: 'var(--color-text)' }}>
                Não tem uma conta?{' '}
              </span>
              <button
                onClick={() => navigate('/signup')}
                className="text-lg font-bold text-white px-4 py-2 rounded-md bg-coral-deep hover:bg-coral-press transition-all shadow-sm"
              >
                Novo cadastro
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-nk-error/10 border border-red-200 text-red-700 px-4 py-3 rounded relative flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {successMessage && (
          <div className="bg-nk-success/10 border border-green-200 text-green-700 px-4 py-3 rounded relative flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {successMessage}
          </div>
        )}

{!showResetPassword && !showMagicLink ? (
          <form className="mt-8 space-y-6 bg-white p-8 rounded-xl shadow-[var(--shadow-3)]" onSubmit={handleSubmit}>
            <div className="rounded-md shadow-sm space-y-4">
              <div>
                <label htmlFor="email-address" className="block text-sm font-medium" style={{ color: 'var(--color-text)' }}>
                  Seu Email
                </label>
                <div className="mt-1 relative">
                  <Mail className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: 'var(--color-text-light)' }} />
                  <input
                    id="email-address"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="appearance-none block w-full pl-10 px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm transition-all"
                    style={{
                      border: '1px solid var(--color-border)',
                      color: 'var(--color-text)'
                    }}
                    placeholder="nome@exemplo.com.br"
                    onFocus={(e) => e.target.style.borderColor = 'var(--salmon)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium" style={{ color: 'var(--color-text)' }}>
                  Senha
                </label>
                <div className="mt-1 relative">
                  <Lock className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: 'var(--color-text-light)' }} />
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="appearance-none block w-full pl-10 px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm transition-all"
                    style={{
                      border: '1px solid var(--color-border)',
                      color: 'var(--color-text)'
                    }}
                    placeholder="••••••••"
                    onFocus={(e) => e.target.style.borderColor = 'var(--salmon)'}
                    onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <button
                  type="button"
                  onClick={() => setShowResetPassword(true)}
                  className="font-medium text-salmon hover:text-salmon-dark transition-colors"
                >
                  Esqueceu sua senha?
                </button>
              </div>
              <div className="text-sm">
                <button
                  type="button"
                  onClick={() => setShowMagicLink(true)}
                  className="font-medium text-salmon hover:text-salmon-dark transition-colors"
                >
                  Entrar sem senha
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-coral-deep hover:bg-coral-press focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                style={{
                  backgroundColor: loading ? 'var(--salmon-muted)' : 'var(--salmon)'
                }}
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </button>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Ou continue com</span>
              </div>
            </div>

            {/* Google Login Button */}
            <div>
              <button
                type="button"
                onClick={handleGoogleLogin}
                className="w-full flex items-center justify-center gap-3 py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-salmon transition-all"
              >
                <Chrome className="w-5 h-5 text-[#4285F4]" />
                Entrar com Google
              </button>
            </div>
          </form>
        ) : showResetPassword ? (
          <form className="mt-8 space-y-6 bg-white p-8 rounded-xl shadow-[var(--shadow-3)]" onSubmit={handleResetPassword}>
            <div>
              <h3 className="text-xl font-semibold mb-4" style={{ color: 'var(--color-text)' }}>
                Recuperar senha
              </h3>
              <p className="text-sm mb-4" style={{ color: 'var(--color-text-light)' }}>
                Digite seu email para receber as instruções de recuperação de senha.
              </p>
            </div>

            <div>
              <label htmlFor="reset-email" className="block text-sm font-medium" style={{ color: 'var(--color-text)' }}>
                Email
              </label>
              <div className="mt-1 relative">
                <Mail className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: 'var(--color-text-light)' }} />
                <input
                  id="reset-email"
                  name="reset-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="appearance-none block w-full pl-10 px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm transition-all"
                  style={{
                    border: '1px solid var(--color-border)',
                    color: 'var(--color-text)'
                  }}
                  placeholder="nome@exemplo.com.br"
                  onFocus={(e) => e.target.style.borderColor = 'var(--salmon)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                />
              </div>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowResetPassword(false);
                  setResetEmail('');
                  setError('');
                  setSuccessMessage('');
                }}
                className="btn-outline-sm flex-1"
              >
                Voltar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-coral-deep hover:bg-coral-press focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                style={{
                  backgroundColor: loading ? 'var(--salmon-muted)' : 'var(--salmon)'
                }}
              >
                {loading ? 'Enviando...' : 'Enviar email'}
              </button>
            </div>
          </form>
        ) : (
          <form className="mt-8 space-y-6 bg-white p-8 rounded-xl shadow-[var(--shadow-3)]" onSubmit={handleMagicLink}>
            <div>
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text)' }}>
                Entrar sem senha
                <Link2 className="w-5 h-5" />
              </h3>
              <p className="text-sm mb-4" style={{ color: 'var(--color-text-light)' }}>
                Digite seu email e enviaremos um link mágico para você entrar sem precisar de senha.
              </p>
            </div>

            <div>
              <label htmlFor="magic-link-email" className="block text-sm font-medium" style={{ color: 'var(--color-text)' }}>
                Email
              </label>
              <div className="mt-1 relative">
                <Mail className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: 'var(--color-text-light)' }} />
                <input
                  id="magic-link-email"
                  name="magic-link-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={magicLinkEmail}
                  onChange={(e) => setMagicLinkEmail(e.target.value)}
                  className="appearance-none block w-full pl-10 px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm transition-all"
                  style={{
                    border: '1px solid var(--color-border)',
                    color: 'var(--color-text)'
                  }}
                  placeholder="nome@exemplo.com.br"
                  onFocus={(e) => e.target.style.borderColor = 'var(--salmon)'}
                  onBlur={(e) => e.target.style.borderColor = 'var(--color-border)'}
                />
              </div>
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowMagicLink(false);
                  setMagicLinkEmail('');
                  setError('');
                  setSuccessMessage('');
                }}
                className="btn-outline-sm flex-1"
              >
                Voltar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-coral-deep hover:bg-coral-press focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                style={{
                  backgroundColor: loading ? 'var(--salmon-muted)' : 'var(--salmon)'
                }}
              >
                {loading ? 'Enviando...' : 'Enviar link mágico'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default SignInForm;